import { ChangeDetectionStrategy, Component, computed, effect, forwardRef, inject, input, model, OnDestroy, OnInit, output, viewChild } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALIDATORS, NG_VALUE_ACCESSOR, NgForm, Validator } from '@angular/forms';
import { ConversationComponent } from 'src/app/controls/components/advanced/conversation/conversation.component';
import { LayoutColumnComponent } from 'src/app/controls/components/basic/layout-column/layout-column.component';
import { LayoutRowComponent } from 'src/app/controls/components/basic/layout-row/layout-row.component';
import { TextboxComponent } from 'src/app/controls/components/basic/textbox/textbox.component';
import { DocumentDirection, FormPartnerConversation, NewCommentEvent, PartnerOnboardingComment, ToggleConversationEvent, Transport, TransportFieldOwner, TransportFieldType, TransportType } from '../../models/partner-onboarding';
import { OnboardingAccordianComponent } from '../onboarding-accordian/onboarding-accordian.component';
import { OnboardingCommentsIconComponent } from '../onboarding-comments-icon/onboarding-comments-icon.component';
import { Conversation, ConversationMessage } from 'src/app/controls/components/advanced/conversation/models/conversation';
import { OnboardingCommentsContainerComponent } from '../onboarding-comments-container/onboarding-comments-container.component';
import { LinkComponent } from 'src/app/controls/components/basic/link/link.component';
import { Subscription } from 'rxjs';
import { GenericModalService, ModalSettingsTemplate } from 'src/app/shared/generic-modal/generic-modal.service';
import { SelectTransportDialogComponent, SelectTransportDialogResult } from '../select-transport-dialog/select-transport-dialog.component';
import { OnboardingFileSelectorComponent } from '../onboarding-file-selector/onboarding-file-selector.component';
import { OnboardingFile } from '../onboarding-file-selector/models/onboarding-file';
import { FormTransportField, OnboardingDropdownConfiguration } from './models/transport';
import { SingleselectboxComponent } from 'src/app/controls/components/basic/singleselectbox/singleselectbox.component';
import { OnboardingCredentialsComponent } from '../onboarding-credentials/onboarding-credentials.component';
import { OnboardingCredentials } from '../onboarding-credentials/models/credentials';
import { PasswordComponent, PasswordComponentValue } from 'src/app/controls/components/basic/password/password.component';
import { Guid } from 'src/app/shared/utils';

@Component({
  selector: 'app-onboarding-transport',
  standalone: true,
  imports: [FormsModule, LayoutRowComponent, LayoutColumnComponent, TextboxComponent, PasswordComponent, OnboardingAccordianComponent, OnboardingCommentsIconComponent, OnboardingCommentsContainerComponent, ConversationComponent, LinkComponent, OnboardingFileSelectorComponent, SingleselectboxComponent, OnboardingCredentialsComponent],
  templateUrl: './onboarding-transport.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './onboarding-transport.component.scss',
  providers: [
      {
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => OnboardingTransportComponent),
        multi: true
      },
      {
        provide: NG_VALIDATORS,
        useExisting: forwardRef(() => OnboardingTransportComponent),
        multi: true
      }
    ],
})
export class OnboardingTransportComponent implements ControlValueAccessor, Validator, OnInit, OnDestroy {  
  modalService = inject(GenericModalService);
  form = viewChild<NgForm>('form');
  possibleTransports = input.required<Transport[]>();
  model = model<Transport>();
  documentDirection = input<DocumentDirection>(DocumentDirection.Inbound);
  readonly = input<boolean>(false);
  disabled = input<boolean>(false);
  formChangesSubscription: Subscription;
  showTransportDetails = false;
  transportChanged = false;
  requestCredentialsClicked = output<TransportType>();
  showCredentialsClicked = output<TransportType>();

  //Conversation related inputs and outputs
  conversation = input<FormPartnerConversation | null>(null);
  readonlyConversation = input<boolean>(false);
  allowConversation = input<boolean>(true);
  unreadConversationIds = input<string[]>([]);
  autoRefreshEnabled = input<boolean>(false);
  toggleConversationClicked = output<ToggleConversationEvent>();
  toggleAutoRefreshClicked = output<void>();
  newCommentClicked = output<NewCommentEvent>();
  conversationAreaId =  input<string>(Guid.newGuid());

  constructor() {
    // Once opened due to unread messages, keep it open even after the messages are read
    effect(() => {
      if (this.hasFieldsWithUnreadMessages()) {
        this.showTransportDetails = true;
      }
    });
  }

  ngOnInit(): void {
    this.formChangesSubscription = this.form().form.valueChanges.subscribe(x => {
      const transport = this.getSelectedTransport(x);
      this.onChanged(transport);
    })
  }

  selectedInputTransport = computed(() => {
    if (!this.model()) return null;
    return this.possibleTransports().find(t => t.Type === this.model().Type);
  });

  // Name of the selected transport type (e.g., SFTP, FTP, AS2)
  transportTypeName = computed(() => {
    switch (this.model()?.Type) {
      case TransportType.SFTPCustomerEndpoint:
      case TransportType.SFTPPartnerEndpoint:
      case TransportType.IntegratedSFTP:
        return 'SFTP';
      case TransportType.FTPCustomerEndpoint:
      case TransportType.FTPPartnerEndpoint:
      case TransportType.IntegratedFTP:
        return 'FTP';
      case TransportType.AS2:
        return 'AS2';
      default:
        return 'Unknown';
    }
  });

  // Description of transport action based on direction and endpoint owner
  transportActionDescription = computed(() => {
    if (this.documentDirection() === DocumentDirection.Inbound) {
      if (this.model()?.Type === TransportType.SFTPCustomerEndpoint || this.model()?.Type === TransportType.FTPCustomerEndpoint || this.model()?.Type === TransportType.IntegratedFTP || this.model()?.Type === TransportType.IntegratedSFTP) {
        return 'You will <b>push</b> documents to the designated folder on our endpoint';
      }
      if (this.model()?.Type === TransportType.SFTPPartnerEndpoint || this.model()?.Type === TransportType.FTPPartnerEndpoint) {
        return 'We will monitor your endpoint and <b>pull</b> documents in an automated schedule';
      }
      if (this.model()?.Type === TransportType.AS2) {
        return 'You will send documents to our AS2 endpoint';
      }
      return '';
    }
    else if (this.documentDirection() === DocumentDirection.Outbound) {
      if (this.model()?.Type === TransportType.SFTPCustomerEndpoint || this.model()?.Type === TransportType.FTPCustomerEndpoint || this.model()?.Type === TransportType.IntegratedFTP || this.model()?.Type === TransportType.IntegratedSFTP) {
        return 'You will monitor our endpoint and <b>pull</b> documents in an automated schedule';
      }
      if (this.model()?.Type === TransportType.SFTPPartnerEndpoint || this.model()?.Type === TransportType.FTPPartnerEndpoint) {
        return 'We will <b>push</b> documents to the designated folder on your endpoint';
      }
      if (this.model()?.Type === TransportType.AS2) {
        return 'We will send documents to your AS2 endpoint';
      }
      return '';
    }
    else {
      return '';
    }
  });

  // Full transport description combining type and action
  transportDescription = computed(() => 'Documents will be transported via <b>' + this.transportTypeName() + '</b>.' + ' ' + this.transportActionDescription());
  
  // Header for connection details based on transport type and ownership
  connectionDetailsHeader = computed(() => {
    const transportType = this.model()?.Type;
    if (transportType === TransportType.SFTPCustomerEndpoint || transportType === TransportType.FTPCustomerEndpoint || transportType === TransportType.IntegratedFTP || transportType === TransportType.IntegratedSFTP) {
      return 'Details for connecting to our ' + this.transportTypeName() + ' endpoint';
    }
    else if (transportType === TransportType.SFTPPartnerEndpoint || transportType === TransportType.FTPPartnerEndpoint) {
      return 'How can we connect to your ' + this.transportTypeName() + ' endpoint?';
    }
    else if (transportType === TransportType.AS2) {
      return 'AS2 Endpoint Details';
    }
    else {
      return 'Endpoint Details';
    }
  });

  // Form representation of transport fields for data binding
  formTransportFields = computed(() => {
    if (!this.selectedInputTransport()) {
      return [];
    }

    const customerTransportFields = this.selectedInputTransport()?.TransportFields.filter(tf => tf.FilledBy === TransportFieldOwner.Customer);    
    const partnerTransportFields = this.model().TransportFields.filter(tf => tf.FilledBy === TransportFieldOwner.Partner);
    const fields = customerTransportFields.concat(partnerTransportFields);

    const formFields = fields.map(field => {
      const ftf = new FormTransportField();
      ftf.Name = field.Name;
      ftf.Presentation = field.Presentation;
      ftf.Type = field.Type;
      ftf.FilledBy = field.FilledBy;
      ftf.DocumentDirection = field.DocumentDirection;
      ftf.ConversationId = field.ConversationId;
      
      if (field.Type === TransportFieldType.File) {
        ftf.Value = this.parseOnboardingFile(field.Value);
        ftf.Configuration = null;
      }
      else if (field.Type === TransportFieldType.Dropdown) {
        ftf.Value = field.Value;
        ftf.Configuration = this.parseDropdownConfiguration(field.Configuration);
      }
      else if (field.Type === TransportFieldType.Password) {
        ftf.Value = this.parseOnboardingPassword(field.Value);
      }
      else if (field.Type === TransportFieldType.Credentials) {
        ftf.Value = this.parseOnboardingCredentials(field.Value);
        ftf.Configuration = null;
      }
      else {
        ftf.Value = field.Value;
        ftf.Configuration = null;
      }
      
      return ftf;
    });
  
    return formFields;
});

hasFieldsWithUnreadMessages = computed(() => this.formTransportFields().some(field => this.hasUnreadMessages(field.ConversationId)));

  async changeTransport() {
    const modalSettings = new ModalSettingsTemplate('lg', 'static');
    const result = await this.modalService.openComponentWithInitialize<SelectTransportDialogResult, SelectTransportDialogComponent>(SelectTransportDialogComponent, modalSettings, dialog => {
      dialog.possibleTransportTypes.set(this.possibleTransports().map(t => t.Type));
      dialog.documentDirection.set(this.documentDirection());
      dialog.currentTransportType.set(this.model() ? this.model().Type : null);
    });

    if (!result || !result.SelectedTransportType) {
      return;
    }

    if (this.model() && this.model().Type === result.SelectedTransportType) {
      return;
    }

    const selectedTransport = this.possibleTransports().find(t => t.Type === result.SelectedTransportType);
    const copy: Transport = structuredClone(selectedTransport);
    this.model.set(copy);

    const transport = this.getSelectedTransport(this.form().form.value);
    this.onChanged(transport);
    this.showTransportDetails = true;
    this.transportChanged = true;
  }

  removeTransport() {
    this.model.set(null);
    this.onChanged(this.model());
  }

  parseOnboardingFile(onboardingFileString: string): OnboardingFile | null {
    if (!onboardingFileString) {
      return null;
    }
    const onboardingFile = JSON.parse(onboardingFileString) as OnboardingFile;
    return onboardingFile;
  }

  parseOnboardingCredentials(onboardingCredentialsString: string): OnboardingCredentials | null {
    if (!onboardingCredentialsString) {
      return null;
    }
    const onboardingCredentials = JSON.parse(onboardingCredentialsString) as OnboardingCredentials;
    return onboardingCredentials;
  }

  parseOnboardingPassword(onboardingPasswordString: string): PasswordComponentValue | null {
    if (!onboardingPasswordString) {
      return null;
    }
    const onboardingPassword = {id: onboardingPasswordString, value: null} as PasswordComponentValue;
    return onboardingPassword;
  }

  parseDropdownConfiguration(configurationString: string): OnboardingDropdownConfiguration | null {
    if (!configurationString) {
      return null;
    }
    const onboardingFile = JSON.parse(configurationString) as OnboardingDropdownConfiguration;
    return onboardingFile;
  }

  getSelectedTransport(formValue: unknown) {
    const transport: Transport = this.model();
    const copy: Transport = structuredClone(transport);
    if (!copy) {
      return null;
    }

    copy.TransportFields = copy.TransportFields?.filter(tf => tf.FilledBy === TransportFieldOwner.Partner) ?? [];
    for (const field of copy.TransportFields ?? []) {
      field.ConversationId = field.ConversationId ?? crypto.randomUUID()

      const fieldName = 'transportField-' + field.Name;
      if (formValue[fieldName] === undefined) {
        continue;
      }

      let value: string = null;
      if (field.Type === TransportFieldType.File) {
        const fieldValue = formValue[fieldName] as OnboardingFile;
        value = JSON.stringify(fieldValue);
      }
      else if (field.Type === TransportFieldType.Credentials) {
        const fieldValue = formValue[fieldName] as OnboardingCredentials;
        value = JSON.stringify(fieldValue);
      }
      else if (field.Type === TransportFieldType.Password) {
        const fieldValue = formValue[fieldName] as PasswordComponentValue;
        value = fieldValue?.value ? fieldValue?.value : fieldValue?.id;
      }
      else {
        const fieldValue = formValue[fieldName] as string;
        value = fieldValue;
      }
      field.Value = value;
    }

    return copy;
  }

  requestCredentials(transportType: TransportType) {
    this.requestCredentialsClicked.emit(transportType);
  }

  showCredentials(transportType: TransportType) {
    this.showCredentialsClicked.emit(transportType);
  }


  // File methods

  downloadFile(onboardingFile: OnboardingFile) {
    if (!onboardingFile) return;
    this.downloadBase64File(onboardingFile.Data, onboardingFile.FileName, onboardingFile.Mime);
  }

  downloadBase64File(base64: string, filename: string, mime = 'application/octet-stream') {
    const parts = base64.split(',');
    const b64 = parts.length > 1 ? parts[1] : parts[0];
  
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  
    const blob = new Blob([bytes], { type: mime });
    const url = URL.createObjectURL(blob);
  
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
  
    URL.revokeObjectURL(url);
  }


  // Conversation methods

  conversationToShow = computed(() => {
    if (!this.conversation()) return null;
    return new Conversation(
      {
        Id: this.conversation().Id, 
        AreaId: this.conversation().AreaId,
        UserId: 'partner',
        Comments: this.conversation().Comments?.map(cm => {
          const userId = cm.IsAction ? 'system' : (cm.FromCustomer ? 'customer' : 'partner');
          return new ConversationMessage({UserId: userId, Message: cm.Message, Timestamp: cm.Timestamp})
      }) ?? [], 
        LastRead: this.conversation().PartnerLastRead
      }
    );
  });

  hasUnreadMessages(conversationId: string): boolean {
    if (!this.unreadConversationIds()) {
      return false;
    }
    return this.unreadConversationIds()?.some(id => id === conversationId);
  }

  toggleConversation(conversationId: string, areaId: string) {
    const event = new ToggleConversationEvent({ConversationId: conversationId, AreaId: areaId});
    this.toggleConversationClicked.emit(event);
  }

  newComment(message: ConversationMessage, conversationId: string) {
    const comment = new PartnerOnboardingComment({FromCustomer: false, Message: message.Message, Timestamp: message.Timestamp});
    const newCommentEvent = new NewCommentEvent({Comment: comment, ConversationId: conversationId});
    this.newCommentClicked.emit(newCommentEvent);
  }
  
  toggleAutoRefresh(): void {
    this.toggleAutoRefreshClicked.emit();
  }


  // ControlValueAccessor interface methods

  writeValue(obj: Transport): void {
    this.model.set(obj);
    this.transportChanged = false;
  }

  onChanged: (value: Transport) => void = () => { };
  registerOnChange(fn: (value: Transport) => void): void {
    this.onChanged = fn;
  }

  onTouched: () => void = () => { };
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.form().form.disable();
    } 
    else {
      this.form().form.enable();
    }
  }

  // Validator interface methods

  validate() {
    if (this.form()?.form.disabled) {
      return [];
    }
    return this.form().form?.valid ? [] : ['Invalid'];
  }

  // OnDestroy interface methods

  ngOnDestroy(): void {
    if (this.formChangesSubscription) {
      this.formChangesSubscription.unsubscribe();
    }
  }
}
